import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

const KycVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get userId from navigation state or URL params
  const userId = location.state?.userid || new URLSearchParams(location.search).get('userid') || '';
  
  const [aadhar, setAadhar] = useState('');
  const [pan, setPan] = useState('');
  const [oldd, setOldd] = useState('');
  const [images, setImages] = useState({
    aadharFront: 'https://wealthmcx.com/assets/kyc/aadhar1.png',
    aadharBack: 'https://wealthmcx.com/assets/kyc/aadhar2.png',
    panCard: 'https://wealthmcx.com/assets/kyc/pan.png',
    selfie: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!userId) {
      alert('User ID not found. Please login again.');
      navigate('/login');
      return;
    }
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      
      const response = await fetch(`https://wealthmcx.com/Rest5/user_details_api`, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      
      setAadhar(data.aadhar || '');
      setOldd(data.aadhar);
      setPan(data.pan_no || '');
      setImages({
        aadharFront: data.aadhar_doc || 'https://wealthmcx.com/assets/kyc/aadhar1.png',
        aadharBack: data.aadhar_doc2 || 'https://wealthmcx.com/assets/kyc/aadhar2.png',
        panCard: data.pan_doc || 'https://wealthmcx.com/assets/kyc/pan.png',
        selfie: data.profile_img || ''
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const pickImage = async (imageType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg, image/png, image/jpg';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Check file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      
      setUploading(true);
      
      const formData = new FormData();
      formData.append('photo', file);
      
      try {
        const response = await fetch('https://wealthmcx.com/Rest5/upload_image_api/', {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json',
          },
        });
        
        const responseJson = await response.json();
        const imageUrl = `https://wealthmcx.com/assets/kyc/${responseJson.img}`;
        
        setImages(prev => ({
          ...prev,
          [imageType]: imageUrl
        }));
        
        alert(`${imageType} uploaded successfully!`);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      } finally {
        setUploading(false);
      }
    };
    
    input.click();
  };

  const handleSubmit = async () => {
    if (!aadhar || !pan || 
        images.aadharFront.includes('aadhar1.png') || 
        images.aadharBack.includes('aadhar2.png') || 
        images.panCard.includes('pan.png')) {
      alert('Please provide all details and upload documents');
      return;
    }
    
    if (aadhar.length !== 12) {
      alert('Please enter a valid 12-digit Aadhar number');
      return;
    }
    
    if (pan.length !== 10) {
      alert('Please enter a valid 10-digit PAN number');
      return;
    }

    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append('aadhar', aadhar);
      formData.append('pan_no', pan);
      formData.append('aadhar_doc', images.aadharFront);
      formData.append('aadhar_doc2', images.aadharBack);
      formData.append('pan_doc', images.panCard);
      formData.append('profile_img', images.selfie);
      formData.append('user_id', userId);

      const response = await fetch(`https://wealthmcx.com/Rest5/web_upload_kyc_api`, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });

      const responseJson = await response.json();
      alert(responseJson.msg);

      if (responseJson.status === 0) {
        if (responseJson.ustatus === "") {
          navigate('/account');
        } else {
          navigate('/login');
        }
      }
    } catch (error) {
      console.error('Error submitting KYC:', error);
      alert('Failed to submit KYC. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderDocumentUpload = (title, imageType, imageUri, isRequired = true) => (
    <div 
      className="bg-white rounded-3 p-3 mb-3 text-center"
      style={{ 
        width: '48%',
        cursor: 'pointer',
        border: '1px solid #f0f0f0',
        transition: 'all 0.2s ease',
        minHeight: 180,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={() => pickImage(imageType)}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#3B82F6';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(59,130,246,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#f0f0f0';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {imageUri && !imageUri.includes('default') && !imageUri.includes('aadhar1.png') && !imageUri.includes('aadhar2.png') && !imageUri.includes('pan.png') ? (
        <>
          <i className="bi bi-check-circle-fill" style={{ fontSize: 24, color: '#3B82F6' }}></i>
          <p className="fw-semibold mt-2 mb-1" style={{ fontSize: 13, color: '#333' }}>
            {title}
          </p>
          <img 
            src={imageUri} 
            alt={title}
            className="mt-2 rounded-2"
            style={{ width: '100%', height: 80, objectFit: 'cover', border: '1px solid #E2E8F0' }}
          />
        </>
      ) : (
        <>
          <i className="bi bi-cloud-upload" style={{ fontSize: 24, color: '#64748B' }}></i>
          <p className="fw-semibold mt-2 mb-1" style={{ fontSize: 13, color: '#333' }}>
            Upload {title}
          </p>
          <p className="text-secondary" style={{ fontSize: 11, marginBottom: 0 }}>
            JPG, PNG (Max 2MB)
          </p>
        </>
      )}
    </div>
  );

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      {/* Loading Overlays */}
      {(loading || uploading) && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="bg-white p-4 rounded-4 text-center shadow-lg">
            <Spinner animation="border" variant="primary" style={{ width: 40, height: 40 }} />
            <div className="mt-3 text-secondary fw-semibold">
              {uploading ? 'Uploading document...' : 'Submitting KYC...'}
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div style={{ backgroundColor: '#3B82F6' }}>
        <div className="d-flex justify-content-between align-items-center px-3 py-3">
          <button 
            onClick={() => navigate(-1)}
            className="btn p-0 d-flex align-items-center justify-content-center"
            style={{ width: 32, height: 32, backgroundColor: 'transparent', border: 'none' }}
          >
            <i className="bi bi-arrow-left text-white" style={{ fontSize: 18 }}></i>
          </button>
          <h6 className="fw-semibold mb-0 text-white" style={{ fontSize: 15 }}>
            KYC Verification
          </h6>
          <div style={{ width: 32 }}></div>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-3 pb-4">
        {/* User ID Display */}
        {userId && (
          <div className="mb-3 text-center">
            <span className="badge bg-primary bg-opacity-10 text-primary py-2 px-3 rounded-pill">
              <i className="bi bi-person-badge me-1"></i> User ID: {userId}
            </span>
          </div>
        )}

        {/* Aadhar Number */}
        <div className="mb-3 mt-2">
          <label className="form-label fw-semibold small mb-1" style={{ color: '#64748B', fontSize: 11 }}>
            AADHAR NUMBER
          </label>
          <input
            type="text"
            className="form-control rounded-2 py-2"
            style={{ fontSize: 12, borderColor: '#e2e8f0' }}
            placeholder="Enter 12-digit Aadhar number"
            maxLength={12}
            value={aadhar}
            onChange={(e) => setAadhar(e.target.value.replace(/\D/g, ''))}
          />
        </div>

        {/* Aadhar Uploads */}
        <div className="d-flex justify-content-between flex-wrap mb-2">
          {renderDocumentUpload('Aadhar Front', 'aadharFront', images.aadharFront)}
          {renderDocumentUpload('Aadhar Back', 'aadharBack', images.aadharBack)}
        </div>

        {/* PAN Number */}
        <div className="mb-3">
          <label className="form-label fw-semibold small mb-1" style={{ color: '#64748B', fontSize: 11 }}>
            PAN NUMBER
          </label>
          <input
            type="text"
            className="form-control rounded-2 py-2"
            style={{ fontSize: 12, borderColor: '#e2e8f0' }}
            placeholder="Enter 10-digit PAN number"
            maxLength={10}
            value={pan}
            onChange={(e) => setPan(e.target.value.toUpperCase())}
          />
        </div>

        {/* PAN and Selfie Uploads */}
        <div className="d-flex justify-content-between flex-wrap mb-3">
          {renderDocumentUpload('PAN Card', 'panCard', images.panCard)}
          {renderDocumentUpload('Selfie Photo', 'selfie', images.selfie)}
        </div>

        {/* Submit Button - Only show if KYC not already submitted */}
        {oldd === '' && (
          <button
            className="btn w-100 py-3 fw-bold rounded-2 mt-2"
            style={{
              backgroundColor: '#3B82F6',
              color: '#FFFFFF',
              fontSize: 13,
              letterSpacing: 0.5,
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(59,130,246,0.25)'
            }}
            onClick={handleSubmit}
            disabled={loading}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#2563EB';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#3B82F6';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {loading ? <Spinner animation="border" size="sm" style={{ color: '#fff' }} /> : 'SUBMIT KYC'}
          </button>
        )}

        {/* KYC Already Submitted Message */}
        {oldd !== '' && (
          <div className="text-center p-3 rounded-2 mt-3" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
            <i className="bi bi-check-circle-fill text-success me-2" style={{ fontSize: 14 }}></i>
            <span className="text-success fw-semibold" style={{ fontSize: 12 }}>KYC Already Submitted</span>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        
        .form-control:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(59,130,246,0.2);
          border-color: #3B82F6;
        }
        
        .btn:focus {
          outline: none;
          box-shadow: none;
        }
        
        input::placeholder {
          font-size: 12px;
        }
      `}</style>
    </div>
  );
};

export default KycVerification;